
import React, { useState, useMemo, useEffect } from 'react';
import type { AppView, BaseProblem, GeneratedProblem, GenerationJob, Worksheet, UserStats } from '../types';
import { useAuth } from '../hooks/useAuth';
import SettingsView from './SettingsView';
import { getUserStats } from '../services/firebaseService';

interface MyPageViewProps {
    onSetView: (view: AppView) => void;
    baseProblems: BaseProblem[];
    problemHistory: GeneratedProblem[];
    generationJobs: GenerationJob[];
    worksheets: Worksheet[];
}

type MyPageTab = 'dashboard' | 'billing' | 'account' | 'settings';

const MyPageView: React.FC<MyPageViewProps> = ({ onSetView, baseProblems, problemHistory, generationJobs, worksheets }) => {
    const [activeTab, setActiveTab] = useState<MyPageTab>('dashboard');
    const [userStats, setUserStats] = useState<UserStats | null>(null);
    const { user } = useAuth();

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onSetView('main');
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onSetView]);

    useEffect(() => {
        if (user) {
            getUserStats(user.uid).then(setUserStats);
        }
    }, [user]);

    const inventoryStats = useMemo(() => ({
        totalProblems: baseProblems.length + problemHistory.length,
        baseProblemsCount: baseProblems.length,
        generatedProblemsCount: problemHistory.length,
        worksheetsCount: worksheets.length,
        pendingJobs: generationJobs.filter(j => j.status === 'pending' || j.status === 'processing').length,
    }), [baseProblems, problemHistory, generationJobs, worksheets]);

    const NavItem: React.FC<{ tabId: MyPageTab, label: string }> = ({ tabId, label }) => (
        <button
            onClick={() => setActiveTab(tabId)}
            className={`w-full text-left px-4 py-2 rounded-md font-semibold transition-colors ${
                activeTab === tabId
                ? 'bg-[var(--color-accent)] text-[var(--color-accent-text)]'
                : 'text-[var(--color-text-secondary)] hover:bg-white/10 hover:text-[var(--color-text-primary)]'
            }`}
        >
            {label}
        </button>
    );

    const StatCard: React.FC<{ title: string; value: number | string; icon: React.ReactNode; subTitle?: string }> = ({ title, value, icon, subTitle }) => (
        <div className="bg-[var(--color-bg-secondary)] p-6 rounded-lg flex items-center gap-4 border border-[var(--color-border)] shadow-sm hover:shadow-md transition-shadow">
            <div className="text-[var(--color-accent)] p-3 bg-[var(--color-accent)]/10 rounded-full">{icon}</div>
            <div>
                <p className="text-sm text-[var(--color-text-secondary)]">{title}</p>
                <p className="text-2xl font-bold text-[var(--color-text-primary)]">{typeof value === 'number' ? value.toLocaleString() : value}</p>
                {subTitle && <p className="text-xs text-[var(--color-text-tertiary)] mt-1">{subTitle}</p>}
            </div>
        </div>
    );

    const renderContent = () => {
        switch (activeTab) {
            case 'dashboard':
                return (
                    <div className="space-y-8">
                        <div>
                            <h2 className="text-2xl font-bold mb-4 text-[var(--color-text-primary)]">현재 보유 현황 (Inventory)</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <StatCard 
                                    title="현재 보유 총 문제" 
                                    value={inventoryStats.totalProblems} 
                                    icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" /></svg>} 
                                    subTitle={`기반 ${inventoryStats.baseProblemsCount} / 변형 ${inventoryStats.generatedProblemsCount}`}
                                />
                                <StatCard 
                                    title="만든 학습지" 
                                    value={inventoryStats.worksheetsCount} 
                                    icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>} 
                                />
                                <StatCard 
                                    title="진행 중인 작업" 
                                    value={inventoryStats.pendingJobs} 
                                    icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>} 
                                />
                            </div>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4 text-[var(--color-text-primary)] border-t border-[var(--color-border)] pt-8">누적 활동 기록 (History)</h2>
                            <p className="text-sm text-[var(--color-text-secondary)] mb-6">사용자가 지금까지 다정AI와 함께 수행한 모든 작업의 누적 기록입니다.</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                <StatCard 
                                    title="생성했던 문제 수" 
                                    value={userStats?.totalProblemsGenerated || 0} 
                                    icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>} 
                                    subTitle="삭제된 문제 포함"
                                />
                                <StatCard 
                                    title="가져왔던 문제 수" 
                                    value={userStats?.totalProblemsImported || 0} 
                                    icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>} 
                                />
                                <StatCard 
                                    title="해설 다시 쓰기" 
                                    value={userStats?.explanationRegenerations || 0} 
                                    icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>} 
                                />
                                <StatCard 
                                    title="수식 교정 수행" 
                                    value={userStats?.mathFixCount || 0} 
                                    icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>} 
                                />
                                <StatCard 
                                    title="학습지 제작 횟수" 
                                    value={userStats?.worksheetsCreated || 0} 
                                    icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>} 
                                />
                                 <StatCard 
                                    title="창작 문제 생성" 
                                    value={userStats?.creativeProblemsGenerated || 0} 
                                    icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>} 
                                />
                            </div>
                        </div>
                    </div>
                );
            case 'billing':
                return (
                    <div className="space-y-6">
                         <h2 className="text-2xl font-bold">요금제 및 결제</h2>
                         <div className="bg-[var(--color-bg-secondary)] p-8 rounded-lg border border-[var(--color-border)] text-center">
                             <h3 className="text-xl font-semibold text-[var(--color-text-primary)]">현재 '무료 플랜'을 사용하고 있습니다.</h3>
                             <p className="mt-2 text-[var(--color-text-secondary)]">정식 유료 플랜은 곧 출시될 예정입니다!</p>
                             <div className="mt-8 bg-black/20 p-6 rounded-lg max-w-sm mx-auto">
                                <p className="text-sm text-[var(--color-text-secondary)]">이번 달 크레딧 사용량 (예시)</p>
                                <p className="text-4xl font-bold text-sky-400 mt-2">7,850 <span className="text-lg">크레딧</span></p>
                                <div className="w-full bg-[var(--color-border)] rounded-full h-2.5 mt-4">
                                    <div className="bg-sky-500 h-2.5 rounded-full" style={{width: '39%'}}></div>
                                </div>
                             </div>
                             <button className="mt-8 bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-white font-bold py-2 px-6 rounded-lg opacity-50 cursor-not-allowed">
                                 요금제 업그레이드 (준비 중)
                             </button>
                         </div>
                    </div>
                );
            case 'account':
                return (
                     <div className="space-y-6">
                        <h2 className="text-2xl font-bold">계정 설정</h2>
                        <div className="bg-[var(--color-bg-secondary)] p-8 rounded-lg border border-[var(--color-border)] max-w-md">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-[var(--color-text-secondary)]">이메일</label>
                                    <p className="mt-1 text-[var(--color-text-primary)]">{user?.email}</p>
                                </div>
                                <div>
                                    <button className="text-sm font-semibold text-sky-400 hover:text-sky-300">비밀번호 변경 (준비 중)</button>
                                </div>
                                <div className="border-t border-[var(--color-border)] pt-6 mt-6">
                                    <button className="text-sm font-semibold text-rose-400 hover:text-rose-300">계정 탈퇴 (준비 중)</button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'settings':
                return (
                    <div className="space-y-6">
                        <h2 className="text-2xl font-bold">표시 설정</h2>
                        <div className="bg-[var(--color-bg-secondary)] p-8 rounded-lg border border-[var(--color-border)] max-w-md">
                            <SettingsView />
                        </div>
                    </div>
                );
        }
    };
    
    return (
        <div className="flex-grow flex overflow-hidden">
            <aside className="w-64 bg-[var(--color-bg-secondary)]/50 p-4 flex-shrink-0 border-r border-[var(--color-border)]">
                <nav className="space-y-2">
                    <NavItem tabId="dashboard" label="대시보드" />
                    <NavItem tabId="billing" label="요금제 및 결제" />
                    <NavItem tabId="account" label="계정 설정" />
                    <NavItem tabId="settings" label="표시 설정" />
                    <div className="border-t border-[var(--color-border)] my-4 !mt-8"></div>
                    <button onClick={() => onSetView('main')} className="w-full text-left px-4 py-2 rounded-md font-semibold text-[var(--color-text-secondary)] hover:bg-white/10 hover:text-[var(--color-text-primary)] transition-colors flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                        라이브러리로 돌아가기
                    </button>
                </nav>
            </aside>
            <main className="flex-grow p-8 overflow-y-auto">
                {renderContent()}
            </main>
        </div>
    );
};

export default MyPageView;
